import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { MeService } from 'src/app/services/me.service';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import {
    ApprovalState,
    ITimesheetFormControls,
    IWeeklyTotalsFormControls,
} from 'src/app/interfaces/timesheets';
import { forkJoin, Observable } from 'rxjs';
import { TimesheetService } from 'src/app/services/timesheet-service.service';
import { MatDialog } from '@angular/material/dialog';
import { Task } from 'src/app/models/task.model';
import { TimesheetDate } from 'src/app/models/timesheet-date-info.model';
import { TimesheetTotalsComponent } from '../timesheet-totals/timesheet-totals.component';
import { WeekHours } from 'src/app/models/week-hours.model';
import { User } from 'src/app/models/User';
import {
    endOfISOWeek,
    getISOWeek,
    getISOWeekYear,
    isSameDay,
    startOfISOWeek,
    sub,
} from 'date-fns';
import { DeleteTimesheetDialogComponent } from './delete-timesheet-dialog/delete-timesheet-dialog.component';
import { NestedSelectComponent } from '../nested-select/nested-select.component';
import { EditTaskDialogComponent } from '../edit-task-dialog/edit-task-dialog.component';
import { MatDividerModule } from '@angular/material/divider';
import { TimesheetItemComponent } from '../timesheet-item/timesheet-item.component';
import { TimesheetControlsComponent } from '../timesheet-controls/timesheet-controls.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgIf, NgFor, NgClass } from '@angular/common';
import { VisibleDirective } from '../../directives/visible.directive';
import { MatCardModule } from '@angular/material/card';
import {
    SUBMISSIONURL,
    WEEK,
    YEAR,
} from 'src/app/constants/queryParamConstants';
import { UniversalSnackbarService } from 'src/app/services/universal-snackbar.service';
import { SnackbarMessages as MSG } from 'src/app/enums/snackBar.enum';
import { daysOfWeek } from 'src/app/constants/timesheetConstants';
import { Day } from 'src/app/types/dayType';

@Component({
    selector: 'app-timesheet-dashboard',
    templateUrl: './timesheet-dashboard.component.html',
    styleUrls: ['./timesheet-dashboard.component.scss'],
    imports: [
        NgIf,
        MatProgressBarModule,
        TimesheetControlsComponent,
        NestedSelectComponent,
        NgFor,
        TimesheetItemComponent,
        MatDividerModule,
        TimesheetTotalsComponent,
        VisibleDirective,
        MatCardModule,
        NgClass,
    ],
})
export class TimesheetDashboardComponent implements OnInit {
    private timesheetService: TimesheetService = inject(TimesheetService);
    private meService: MeService = inject(MeService);
    private editDialog: MatDialog = inject(MatDialog);
    private snackBar: UniversalSnackbarService = inject(
        UniversalSnackbarService,
    );
    private formBuilder: FormBuilder = inject(FormBuilder);

    protected hasNotes: (
        day: Day,
        timesheet: FormGroup<ITimesheetFormControls>,
    ) => boolean = this.timesheetService.hasNotes;
    @ViewChild(NestedSelectComponent)
    nestedSelectComponent!: NestedSelectComponent;
    @ViewChild(TimesheetTotalsComponent)
    timesheetTotalsComponent!: TimesheetTotalsComponent;
    @ViewChild(EditTaskDialogComponent)
    editTaskComponent!: EditTaskDialogComponent;
    @Input() userFormControl?: FormControl<User | null> =
        new FormControl<User | null>(null);

    lastSelectedDate: TimesheetDate;
    user: User = null;
    message: string = undefined;
    isContractor: boolean = undefined;
    loading = true;
    lockedTimesheet = false;
    currentUrl: URL;
    baseUrl: string;

    weekDates: Date[] = Array(7)
        .fill(null)
        .map(() => new Date());
    contractors?: User[] = null;

    weeklyTotalsFormGroup = new FormGroup<IWeeklyTotalsFormControls>({
        ...daysOfWeek.reduce(
            (formControls, day) => {
                formControls[day.name + 'Total'] = new FormControl<number>(0);
                return formControls;
            },
            {} as Record<Day | `${Day}Total`, FormControl>,
        ),
        weekTotal: new FormControl(0),
    });
    timesheetFormArray: FormArray<FormGroup<ITimesheetFormControls>> =
        new FormArray([]);

    weekHours: WeekHours = new WeekHours({
        saved: 0,
        submitted: 0,
        locked: 0,
    });

    constructor() {
        const currentDate = new Date();
        let dateOfConsideration = currentDate;
        if (
            isSameDay(currentDate, startOfISOWeek(currentDate)) &&
            currentDate.getHours() < 12
        ) {
            dateOfConsideration = sub(currentDate, { days: 1 });
        }
        this.lastSelectedDate = new TimesheetDate({
            beginningOfWeek: startOfISOWeek(dateOfConsideration),
            endOfWeek: endOfISOWeek(dateOfConsideration),
            selectedWeek: getISOWeek(dateOfConsideration),
            selectedYear: getISOWeekYear(dateOfConsideration),
        });
    }

    ngOnInit(): void {
        this.currentUrl = new URL(window.location.href);
        this.baseUrl = `${this.currentUrl.protocol}//${this.currentUrl.hostname}:${this.currentUrl.port}/submitted-timesheets`;

        if (!this.userFormControl.value) {
            this.meService.get([]).subscribe((result: User) => {
                this.user = result;
                this.getTimesheets();
            });
        } else {
            this.user = this.userFormControl.value;
            this.getTimesheets();
        }

        this.userFormControl.valueChanges.subscribe((observer) => {
            this.user = observer;
            this.getTimesheets();
        });
    }

    protected getPastTasks(): void {
        const queryParams: Map<string, number> = new Map([
            [WEEK, this.lastSelectedDate.selectedWeek - 1],
            [YEAR, this.lastSelectedDate.selectedYear],
        ]);

        if (this.user) {
            this.loading = true;

            this.meService
                .get(['previous-tasks'], queryParams)
                .subscribe((response: Task[]) => {
                    response.forEach((task) => {
                        if (
                            this.timesheetFormArray.controls.find(
                                (timesheet) =>
                                    timesheet.controls.taskId.value == task.id,
                            )
                        ) {
                            //DO NOT CREATE TIMESHEET
                        } else {
                            this.createTimesheet(task);
                        }
                    });
                });
        }

        this.loading = false;
    }

    protected setMessage(): void {
        let hoursNeedSaved = false;
        let hoursNeedSubmitted = false;
        const date = new Date();

        this.timesheetFormArray.controls.forEach(
            (timesheetForm: FormGroup<ITimesheetFormControls>) => {
                if (!timesheetForm.controls.submitted.value) {
                    hoursNeedSubmitted = true;

                    if (!timesheetForm.controls.saved.value) {
                        hoursNeedSaved = true;
                    }
                }
            },
        );

        if (hoursNeedSaved) {
            this.message = 'You have unsaved hours!';
        } else if (hoursNeedSubmitted) {
            this.message = 'You have unsubmitted hours!';
        } else if (this.lastSelectedDate.beginningOfWeek > date) {
            this.message = 'Caution: Timesheet contains future dates.';
        } else {
            this.message = null;
        }
    }

    protected updateDate(date: TimesheetDate): void {
        this.lastSelectedDate = date;
        this.getTimesheets();
    }

    protected getTimesheets(): void {
        const queryParams: Map<string, number> = new Map([
            [WEEK, this.lastSelectedDate.selectedWeek],
            [YEAR, this.lastSelectedDate.selectedYear],
        ]);

        if (this.user) {
            this.loading = true;
            this.getTotals();
            this.setWeekHours();
            this.timesheetService
                .getTimesheets(['user', this.user.userLoginId], queryParams)
                .subscribe({
                    next: (response) => {
                        this.getTimesheetsCallback(response);
                        this.loading = false;
                    },
                    error: () => {
                        this.loading = false;
                    },
                });
        }
    }

    protected createTimesheet(task: Task): void {
        const newForm = this.formBuilder.group<ITimesheetFormControls>({
            id: new FormControl<string | null>(null),
            projectId: new FormControl<string>(task.projectId),
            taskId: new FormControl<string>(task.id),
            week: new FormControl(this.lastSelectedDate.selectedWeek),
            year: new FormControl(this.lastSelectedDate.selectedYear),
            ...this.timesheetService.dayControls(),
            taskName: new FormControl<string>(task.name),
            projectName: new FormControl<string>(task.projectName),
            usersTasksId: new FormControl<string>(''),
            clientName: new FormControl<string>(task.clientName),
            userProfileId: new FormControl<string>(this.user.id),
            firstName: new FormControl<string>(this.user.firstName),
            lastName: new FormControl<string>(this.user.lastName),
            submitted: new FormControl<boolean>(false),
            locked: new FormControl<boolean>(false),
            saved: new FormControl<boolean>(false),
            notesRequired: new FormControl<boolean>(task.notesRequired),
            approvalState: new FormControl<ApprovalState>('New'),
        });
        newForm.markAllAsTouched(); // This change makes the form field red as soon as a bad value is inserted into the field
        this.timesheetFormArray.push(newForm);
        this.nestedSelectComponent.filterAddedTasks();
    }

    private getTimesheetsCallback(
        response: FormGroup<ITimesheetFormControls>[],
    ): void {
        if (response.length > 0) {
            response.forEach((timesheet) => {
                if (timesheet.controls.locked.value) {
                    this.lockedTimesheet = true;
                } else {
                    this.lockedTimesheet = false;
                }
            });
        } else {
            this.lockedTimesheet = false;
        }

        this.timesheetFormArray = new FormArray(response);

        this.getTotals();
        this.setWeekHours();
        this.nestedSelectComponent.getGroupedTasks();
        this.setMessage();
        this.loading = false;
    }

    protected deleteTimesheetForm(
        timesheetToRemove: FormGroup<ITimesheetFormControls>,
    ): void {
        const index =
            this.timesheetFormArray.controls.indexOf(timesheetToRemove);
        if (index !== -1) {
            // indexOf returns -1 if the value isn't found
            this.timesheetFormArray.removeAt(index);
        }
        if (
            timesheetToRemove.controls.saved.value ||
            timesheetToRemove.controls.submitted.value
        ) {
            this.timesheetService
                .delete([timesheetToRemove.controls.id.value])
                .subscribe();
        }
        this.getTotals();
        this.setWeekHours();
        this.setMessage();
        this.nestedSelectComponent.getGroupedTasks();
    }

    public getTotals(): void {
        if (this.timesheetTotalsComponent != null) {
            this.timesheetTotalsComponent.calcTotals(
                this.timesheetFormArray.controls,
            );
        }
    }

    setWeekHours(): void {
        this.weekHours = new WeekHours({
            saved: 0,
            submitted: 0,
            locked: 0,
        });

        if (this.timesheetFormArray.controls != null) {
            this.timesheetFormArray.controls.forEach((timesheetForm) => {
                var totalHours = 0;
                daysOfWeek.forEach(
                    (day) =>
                        (totalHours += timesheetForm.controls[day.name].value),
                );
                this.weekHours.locked += timesheetForm.controls.locked.value
                    ? totalHours
                    : 0;
                this.weekHours.saved += timesheetForm.controls.saved.value
                    ? totalHours
                    : 0;
                this.weekHours.submitted += timesheetForm.controls.submitted
                    .value
                    ? totalHours
                    : 0;
            });
        }
    }

    protected submitTimesheets(): void {
        this.loading =
            this.timesheetFormArray.controls.length > 0 ? true : false;

        const pendingRequests: Observable<unknown>[] = [];

        this.timesheetFormArray.controls.forEach(
            (timesheetForm: FormGroup<ITimesheetFormControls>) => {
                if (timesheetForm.invalid) {
                    this.loading = false;
                    return;
                }
                timesheetForm.controls.submitted.setValue(true);

                daysOfWeek.forEach((day) => {
                    timesheetForm.controls[day.name.toLowerCase()].value ??= 0;
                });
                const queryParams = new Map<string, string>();
                if (timesheetForm.controls.id.value == null) {
                    queryParams.set(SUBMISSIONURL, this.baseUrl);
                    pendingRequests.push(
                        this.timesheetService.post(
                            [],
                            timesheetForm.getRawValue(),
                            queryParams,
                        ),
                    );
                } else if (timesheetForm.controls.id.value != null) {
                    queryParams.set(
                        SUBMISSIONURL,
                        `${this.baseUrl}?id=${timesheetForm.controls.id.value}`,
                    );

                    if (
                        timesheetForm.dirty &&
                        timesheetForm.controls.approvalState.value != '' &&
                        timesheetForm.controls.approvalState.value != 'New'
                    ) {
                        timesheetForm.controls.approvalState.setValue(
                            'Updated',
                        );
                    }
                    pendingRequests.push(
                        this.timesheetService.put(
                            [timesheetForm.controls.id.value],
                            timesheetForm.getRawValue(),
                            queryParams,
                        ),
                    );
                }
            },
        );

        forkJoin(pendingRequests).subscribe({
            next: () => {
                this.getTimesheets();
                this.snackBar.open(MSG.TIMESHEET_SUBMITTED);
            },
            error: () => {
                this.snackBar.open(MSG.TIMESHEET_SUBMITTED_F);
            },
        });
        this.setMessage();
    }

    protected saveTimesheets(): void {
        this.loading =
            this.timesheetFormArray.controls.length > 0 ? true : false;

        const pendingRequests: Observable<unknown>[] = [];

        this.timesheetFormArray.controls.forEach(
            (timesheetForm: FormGroup<ITimesheetFormControls>) => {
                if (timesheetForm.invalid) {
                    this.loading = false;
                    return;
                }

                timesheetForm.controls.saved.setValue(true);

                daysOfWeek.forEach((day) => {
                    timesheetForm.controls[day.name.toLowerCase()].value ??= 0;
                });

                if (timesheetForm.controls.id.value == null) {
                    pendingRequests.push(
                        this.timesheetService.post(
                            [],
                            timesheetForm.getRawValue(),
                        ),
                    );
                } else if (timesheetForm.controls.id.value != null) {
                    if (
                        timesheetForm.dirty &&
                        timesheetForm.controls.approvalState.value != '' &&
                        timesheetForm.controls.approvalState.value != 'New'
                    ) {
                        timesheetForm.controls.approvalState.setValue(
                            'Updated',
                        );
                    }
                    pendingRequests.push(
                        this.timesheetService.put(
                            [timesheetForm.controls.id.value],
                            timesheetForm.getRawValue(),
                        ),
                    );
                }
            },
        );

        forkJoin(pendingRequests).subscribe({
            next: () => {
                this.getTimesheets();
                this.snackBar.open(MSG.TIMESHEET_SAVED);
            },
            error: () => {
                this.snackBar.open(MSG.TIMESHEET_SAVED_F);
            },
        });
        this.setMessage();
    }

    protected openEditTaskDialog(): void {
        const dialogRef = this.editDialog.open(EditTaskDialogComponent, {
            width: '40%',
            data: this.user,
        });

        dialogRef.beforeClosed().subscribe(() => {
            if (dialogRef.componentInstance.updated === true) {
                this.nestedSelectComponent.getGroupedTasks();
                this.getTimesheets();
            }
        });

        dialogRef
            .afterClosed()
            .subscribe(() => this.nestedSelectComponent.getGroupedTasks());
    }

    openTimesheetRemoveDialog(
        timesheetToRemove: FormGroup<ITimesheetFormControls>,
    ): void {
        const dialogRef = this.editDialog.open(DeleteTimesheetDialogComponent);

        dialogRef.afterClosed().subscribe((response) => {
            if (response) {
                this.deleteTimesheetForm(timesheetToRemove);
            }
        });
    }
}
